/**
 * Product page interactions:
 *   • thumbnail → main image swap
 *   • quantity ± buttons (clamped to ≥ 1)
 *   • variant pickers → hidden <select> sync + add-to-cart enabled state
 */
(() => {
  const init = (root) => {
    // ── Gallery ────────────────────────────────────────────────────────
    const mainImg = root.querySelector('[data-product-media-target]');
    const thumbs = root.querySelectorAll('[data-product-thumb]');
    thumbs.forEach((btn) => {
      btn.addEventListener('click', () => {
        const src = btn.dataset.productThumb;
        if (mainImg && src) {
          mainImg.src = src;
          mainImg.removeAttribute('srcset');
        }
        thumbs.forEach((t) => t.classList.toggle('is-active', t === btn));
      });
    });

    // ── Quantity stepper ───────────────────────────────────────────────
    const qty = root.querySelector('[data-qty-input]');
    const setQty = (v) => {
      if (!qty) return;
      qty.value = Math.max(1, Math.floor(v) || 1);
    };
    root.querySelector('[data-qty-decrement]')?.addEventListener('click', () => {
      if (qty) setQty(Number(qty.value || 1) - 1);
    });
    root.querySelector('[data-qty-increment]')?.addEventListener('click', () => {
      if (qty) setQty(Number(qty.value || 1) + 1);
    });

    // ── Variant pickers ────────────────────────────────────────────────
    const select = root.querySelector('[data-product-variant-select]');
    const cta = root.querySelector('[data-add-to-cart]');
    const availability = root.querySelector('[data-product-availability]');
    const stockState = root.querySelector('[data-product-stock-state]');
    const stockLabel = root.querySelector('[data-product-stock-label]');

    /**
     * Repaint the visible stock pill for the selected variant.
     * Mirrors the three states rendered server-side in main-product.liquid:
     * out → low (tracked and at/below threshold) → in.
     */
    const syncStock = (opt) => {
      if (!stockState || !stockLabel) return;
      const available = opt.dataset.variantAvailable === 'true';
      const tracked = opt.dataset.variantTracked === 'true';
      const qty = parseInt(opt.dataset.variantInventory, 10);
      const threshold = parseInt(stockState.dataset.lowThreshold, 10) || 0;

      let state = 'in';
      let text = stockState.dataset.labelIn || '';
      if (!available) {
        state = 'out';
        text = stockState.dataset.labelOut || '';
      } else if (tracked && threshold > 0 && Number.isFinite(qty) && qty > 0 && qty <= threshold) {
        state = 'low';
        text = `${stockState.dataset.labelLow || ''} (${qty})`;
      }

      stockState.classList.remove(
        'main-product__stock-state--in',
        'main-product__stock-state--low',
        'main-product__stock-state--out'
      );
      stockState.classList.add(`main-product__stock-state--${state}`);
      stockLabel.textContent = text;
    };
    const priceWrap = root.querySelector('[data-product-price]');
    // Croatian SR string mirrors the markup default; kept inline here so
    // the announcement re-fires when the selection changes (assigning the
    // same textContent is a no-op for AT, so we clear then set).
    const OOS_MESSAGE = 'Trenutno nedostupno';

    // Server-side `| money` output may contain HTML entities (e.g. &nbsp;), so
    // we set via textContent on prebuilt elements rather than concatenating
    // into innerHTML.
    const syncPrice = (opt) => {
      if (!priceWrap) return;
      const price = opt.dataset.variantPrice || '';
      const compare = opt.dataset.variantCompareAtPrice || '';
      const onSale = opt.dataset.variantOnSale === 'true';
      const pct = opt.dataset.variantSavingsPct || '';
      const savingsLabel = priceWrap.dataset.savingsLabel || '';

      // Reuse existing nodes when present to minimise reflow.
      priceWrap.textContent = '';
      if (onSale) {
        const sale = document.createElement('span');
        sale.className = 'main-product__price-sale';
        sale.textContent = price;
        priceWrap.appendChild(sale);

        if (compare) {
          const cmp = document.createElement('s');
          cmp.className = 'main-product__price-compare';
          cmp.textContent = compare;
          priceWrap.appendChild(cmp);
        }

        if (pct && pct !== '0') {
          const badge = document.createElement('span');
          badge.className = 'main-product__price-badge';
          badge.textContent = `${savingsLabel} ${pct}%`;
          priceWrap.appendChild(badge);
        }
      } else {
        const reg = document.createElement('span');
        reg.className = 'main-product__price-regular';
        reg.textContent = price;
        priceWrap.appendChild(reg);
      }
    };

    // Sidrena cijena + 30-day lowest price (snippets/anchor-price.liquid).
    // The texts are rendered per variant into data attributes on the hidden
    // select, so a variant change only swaps text, no extra request.
    const anchorWrap = root.querySelector('[data-anchor-price]');
    const syncAnchor = (opt) => {
      if (!anchorWrap) return;
      const anchor = opt.dataset.variantAnchor || '';
      const lowest = opt.dataset.variantLowest || '';
      const anchorLine = anchorWrap.querySelector('[data-anchor-price-anchor]');
      const lowestLine = anchorWrap.querySelector('[data-anchor-price-lowest]');
      if (anchorLine) {
        anchorLine.textContent = anchor;
        anchorLine.hidden = !anchor;
      }
      if (lowestLine) {
        lowestLine.textContent = lowest;
        lowestLine.hidden = !lowest;
      }
      anchorWrap.hidden = !anchor && !lowest;
    };

    // Cijena za jedinicu mjere (snippets/unit-price.liquid), per variant from
    // data-variant-unit-price on the hidden select, like the anchor price.
    const unitWrap = root.querySelector('[data-unit-price]');
    const syncUnitPrice = (opt) => {
      if (!unitWrap) return;
      const text = opt.dataset.variantUnitPrice || '';
      const value = unitWrap.querySelector('[data-unit-price-value]');
      if (value) value.textContent = text;
      unitWrap.hidden = !text;
    };

    // EAN / SKU line under the description, from data-variant-barcode / -sku.
    const codesWrap = root.querySelector('[data-product-codes]');
    const syncCodes = (opt) => {
      if (!codesWrap) return;
      const barcode = opt.dataset.variantBarcode || '';
      const sku = opt.dataset.variantSku || '';
      const showSku = Boolean(sku) && sku !== barcode;
      const barcodeWrap = codesWrap.querySelector('[data-product-barcode-wrap]');
      const skuWrap = codesWrap.querySelector('[data-product-sku-wrap]');
      codesWrap.querySelector('[data-product-barcode]').textContent = barcode;
      codesWrap.querySelector('[data-product-sku]').textContent = sku;
      barcodeWrap.hidden = !barcode;
      skuWrap.hidden = !showSku;
      codesWrap.hidden = !barcode && !showSku;
    };

    const syncVariant = () => {
      if (!select) return;
      const chosen = {};
      // Options render either as a radio pill row or, for `Nijansa`, as a
      // <select>. Walking [data-product-option] in DOM order keeps the key
      // insertion order aligned with the variant title's `A / B` order below.
      root.querySelectorAll('[data-product-option]').forEach((el) => {
        if (el.tagName === 'SELECT') {
          chosen[el.name] = el.value;
        } else if (el.checked) {
          chosen[el.name] = el.value;
        }
      });
      const chosenValues = Object.values(chosen);
      for (const opt of select.options) {
        const variantValues = opt.textContent.split(' / ').map((s) => s.trim());
        const match =
          chosenValues.length === variantValues.length &&
          chosenValues.every((v, i) => v === variantValues[i]);
        if (match) {
          opt.selected = true;
          const available = opt.dataset.variantAvailable === 'true';
          if (cta) {
            if (available) {
              cta.removeAttribute('disabled');
              cta.removeAttribute('aria-disabled');
            } else {
              cta.setAttribute('disabled', '');
              cta.setAttribute('aria-disabled', 'true');
            }
          }
          if (availability) {
            // Clear first so SR re-announces even if message text is unchanged
            // between variant flips (some AT collapses identical updates).
            availability.textContent = '';
            if (!available) availability.textContent = OOS_MESSAGE;
          }

          syncPrice(opt);
          syncAnchor(opt);
          syncUnitPrice(opt);
          syncStock(opt);
          syncCodes(opt);

          // Swap the main gallery image to the variant's featured media (if it has one).
          // Click the matching thumb so the active-state stays consistent with the gallery.
          const mediaId = opt.dataset.variantMediaId;
          if (mediaId) {
            const matchingThumb = root.querySelector(`[data-product-thumb-id="${mediaId}"]`);
            if (matchingThumb) {
              matchingThumb.click();
            }
          }
          break;
        }
      }
    };

    // Delegate to the section root so the handler fires for any
    // [data-product-option] regardless of when it was inserted.
    root.addEventListener('change', (e) => {
      if (!e.target.closest('[data-product-option]')) return;
      syncVariant();
    });
  };

  const ready = () => document.querySelectorAll('[data-product]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();
